import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Formik, Form, Field, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline'
import { useAuth } from '../../contexts/AuthContext'
import images from "../../components/images"

function Login() {
  const [showPassword, setShowPassword] = useState(false)
  const { login, loading } = useAuth()
  const navigate = useNavigate()

  // Validation schema using Yup
  const validationSchema = Yup.object({
    email: Yup.string()
      .email('Invalid email address')
      .required('Email is required'),
    password: Yup.string().required('Password is required'),
  })

  const initialValues = {
    email: '',
    password: '',
  }

  const onSubmit = async (values, { setSubmitting }) => {
    try {
      await login(values)
      setSubmitting(false)
    } catch (error) {
      setSubmitting(false)
    }
  }

  return (
    <>
      {/* Top Bar */}
      <div className="fixed top-0 left-0 w-full bg-brandBlue border-b-8 border-brandYellow py-5 z-10"></div>

      <div className="min-h-screen flex items-center justify-center bg-gray-50 ">
        <div className="p-[80px] max-w-[640px] w-full space-y-8 bg-white rounded-[20px] shadow-[0px_0px_20px_0px_#0000001A]">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <img src={images.logo} alt="Future Forward Logo" className="w-100" />
          </div>

          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
          >
            {({ isSubmitting, setFieldValue }) => (
              <Form className="mt-8 space-y-6">
                <div className="space-y-4">
                  {/* Email Field */}
                  <div className="relative">
                    <label htmlFor="email" className="block text-[#717576] font-opensans text-base">
                      Email or User Name
                    </label>
                    <Field
                      type="email"
                      name="email"
                      placeholder="Enter Your Email or User Name"
                      className="input mt-1 rounded-[10px] py-[17px] px-4 bg-[#7175760F] focus:outline-none focus:ring-2 focus:ring-[#7175760F] text[15px] placeholder:text-[15px]"
                    />
                    <ErrorMessage
                      name="email"
                      component="p"
                      className="mt-1 text-sm text-red-600 absolute"
                    />
                  </div>

                  {/* Password Field */}
                  <div className="py-[10px] relative">
                    <label htmlFor="password" className="block font-medium text-[#717576] font-opensans text-base">
                      Password
                    </label>
                    <div className="mt-1 relative">
                      <Field
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        placeholder="Enter Your Password"
                        className="input pr-10 mt-1 rounded-[10px] py-[17px] px-4 bg-[#7175760F] focus:outline-none focus:ring-2 focus:ring-[#7175760F] text[15px] placeholder:text-[15px]"
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <EyeIcon className="h-5 w-5 text-gray-400" />
                        ) : (
                          <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                        )}
                      </button>
                    </div>
                    <ErrorMessage
                      name="password"
                      component="p"
                      className="mt-1 text-sm text-red-600 absolute"
                    />
                  </div>

                  {/* Forgot Password */}
                  <div className="flex justify-end mb-4">
                    <Link to="/forget-password" className="text-[15px] text-brandBlue hover:underline">
                      Forgot Password ?
                    </Link>
                  </div>
                </div>

                {/* Submit Button */}
                <div>
                  <button
                    type="submit"
                    disabled={isSubmitting || loading}
                    className="btn-primary bg-brandBlue w-full py-[17px] px-4 text-base"
                  >
                    {loading ?
                      <>
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white-600 mr-2"></div>
                        Signing in...
                      </>
                      : 'Log In'}
                  </button>
                </div>

                {/* Footer */}
                <div className="text-center pt-[1.5rem]">
                  <p className="text[15px] text-brandBlue hover:text-primary-500">
                    For Authorized Users Only
                  </p>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="fixed bottom-0 left-0 w-full bg-brandBlue border-t-8 border-brandYellow py-5 z-10"></div>
    </>
  )
}

export default Login
